import { Autocomplete, Box, FormControl, TextField } from '@mui/material';
import React, { useState } from 'react';
import { ChipDisplay } from './ChipDisplay';
import type { AutocompleteOption, CustomAutocompleteProps } from './types';
import { VirtualizedListbox } from './VirtualizedListbox';

// Simple listbox without complex virtualization
const SimpleListbox = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLElement>>(
  function SimpleListbox(props, ref) {
    return (
      <div
        ref={ref}
        {...props}
        style={{
          width: '100%',
          maxHeight: '400px',
          overflow: 'auto',
          padding: 0,
          margin: 0,
        }}
      >
        {props.children}
      </div>
    );
  }
);

export function CustomAutocomplete<T extends AutocompleteOption>({
  options,
  value,
  onChange,
  getOptionLabel = (option: T) => option.label,
  getOptionId = (option: T) => option.id,
  renderOption,
  renderChip,
  placeholder = 'Search and select...',
  label,
  disabled = false,
  loading = false,
  maxItems,
  maxItemsMessage,
  enableVirtualization = false,
  filterOptions,
  sx,
  chipSx = {},
  autocompleteProps = {},
}: CustomAutocompleteProps<T>) {
  const [inputValue, setInputValue] = useState('');

  const handleChange = (_event: React.SyntheticEvent, newValue: T[]) => {
    // Don't allow selection beyond the limit
    if (maxItems && newValue.length > maxItems) {
      return;
    }
    onChange(newValue);
  };

  const handleRemove = (itemToRemove: T) => {
    const updatedItems = value.filter(item => getOptionId(item) !== getOptionId(itemToRemove));
    onChange(updatedItems);
  };

  const isInputDisabled = disabled || (maxItems && value.length >= maxItems);

  const getPlaceholder = () => {
    if (isInputDisabled && maxItems) {
      return `Maximum ${maxItems} items selected`;
    }
    if (value.length === 0) return placeholder;
    return 'Add more items...';
  };

  const defaultFilterOptions = (options: T[], state: { inputValue: string }) => {
    if (!state.inputValue.trim()) return options;

    const searchTerm = state.inputValue.toLowerCase();
    return options.filter(option => getOptionLabel(option).toLowerCase().includes(searchTerm));
  };

  const defaultRenderOption = (props: React.HTMLAttributes<HTMLLIElement>, option: T) => (
    <Box
      component="li"
      {...Object.fromEntries(Object.entries(props).filter(([k]) => k !== 'key'))}
      key={`autocomplete-option-${getOptionId(option)}`}
      sx={{
        width: '100% !important',
        height: '100% !important',
        minHeight: 32,
        maxHeight: 32,
        display: 'flex !important',
        alignItems: 'center',
        cursor: 'pointer',
        margin: '0 !important',
        padding: '2px 8px !important',
        boxSizing: 'border-box',
        position: 'relative',
        '&:hover': { backgroundColor: theme => theme.chart.primary.hover },
        '&[aria-selected="true"]': {
          backgroundColor: theme => theme.chart.primary.selected,
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          width: '100%',
        }}
      >
        {getOptionLabel(option)}
      </Box>
    </Box>
  );

  return (
    <FormControl sx={sx || {}} fullWidth>
      <Autocomplete
        data-testid="autocomplete"
        multiple
        value={value}
        onChange={handleChange}
        inputValue={inputValue}
        onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
        options={options}
        getOptionLabel={getOptionLabel}
        getOptionKey={getOptionId}
        filterOptions={filterOptions || defaultFilterOptions}
        loading={loading}
        disabled={Boolean(isInputDisabled)}
        disableCloseOnSelect
        clearOnEscape
        renderTags={() => null}
        renderOption={renderOption || defaultRenderOption}
        ListboxComponent={enableVirtualization ? VirtualizedListbox : SimpleListbox}
        aria-label="Multi-select autocomplete"
        componentsProps={{
          popper: {
            'aria-live': 'polite',
          },
        }}
        renderInput={params => {
          const { size, InputLabelProps: _InputLabelProps, ...restParams } = params;
          return (
            <TextField
              {...restParams}
              {...(size && { size })}
              label={label}
              placeholder={getPlaceholder()}
              variant="outlined"
              fullWidth
              inputProps={{
                ...restParams.inputProps,
                'aria-label': 'Search for items to select',
                'aria-autocomplete': 'list',
                'aria-expanded': false,
                role: 'combobox',
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  paddingTop: value.length > 0 ? '8px' : '0',
                  minHeight: '56px',
                },
              }}
            />
          );
        }}
        {...autocompleteProps}
      />

      {value.length > 0 && (
        <ChipDisplay
          items={value}
          onRemove={handleRemove}
          {...(renderChip ? { renderChip } : {})}
          chipSx={chipSx}
          {...(maxItems !== undefined ? { maxItems } : {})}
          {...(maxItemsMessage !== undefined ? { maxItemsMessage } : {})}
        />
      )}
    </FormControl>
  );
}
